import * as XLSX from 'xlsx';
import { getTicketSeats } from './seatConflictUtils';
import { formatUserFullName, isTicketRefunded } from './ticketsUtils';

const MAX_PARTIES = 4;

const conflictTypeLabel = (type) => {
  const map = {
    overlap: 'Çakışma (aynı koltukta birden fazla bilet)',
    missing_layout: 'Layout’ta yok',
    product_mismatch: 'Ürün uyuşmazlığı',
    sold_without_ticket: 'Satılmış / bilet yok',
    ticket_without_sold_flag: 'Bilet var / satılmış flag yok',
    stock_fb_mismatch: '2. yer uyumsuz',
  };
  return map[type] || type || '';
};

const ticketParty = (ticket, seatId) => {
  if (!ticket) {
    return { name: '', phone: '', pnr: '', email: '' };
  }
  const seat = getTicketSeats(ticket).find((s) => String(s?.id) === String(seatId));
  const title = seat?.selectedOption?.title;
  const product =
    (title && typeof title === 'object' ? title.tr || title.en : '') ||
    (ticket.product?.title
      ? typeof ticket.product.title === 'object'
        ? ticket.product.title.tr || ticket.product.title.en
        : String(ticket.product.title)
      : '');

  return {
    name: formatUserFullName(ticket.user) || ticket.user?.email || '',
    phone: ticket.user?.mobile || '',
    pnr: ticket.pnr || ticket.id || '',
    email: ticket.user?.email || '',
    product,
  };
};

const findTicket = (tickets, id) =>
  (tickets || []).find((t) => String(t.id) === String(id));

/**
 * Sadece çakışma raporu.
 * Satır: Koltuk | Tip | (Kişi/Telefon/PNR) x N yan yana | Aktarılacak koltuk | Not
 */
export function exportSeatOpsExcel({ event, eventID, tickets = [], analysis }) {
  const conflicts = analysis?.conflicts || [];
  const seatsByTicket = analysis?.seatsByTicket || new Map();

  if (!conflicts.length) {
    throw new Error('Çakışma yok — rapor üretilecek kayıt bulunamadı');
  }

  // Aynı koltukta birden fazla conflict olabilir → koltuk bazında birleştir
  const bySeat = new Map();
  conflicts.forEach((c) => {
    if (!c?.seatId) return;
    const prev = bySeat.get(c.seatId) || {
      seatId: c.seatId,
      types: new Set(),
      messages: [],
      ticketIds: new Set(),
    };
    if (c.type) prev.types.add(c.type);
    if (c.message) prev.messages.push(c.message);
    (c.ticketIds || []).forEach((id) => prev.ticketIds.add(id));
    bySeat.set(c.seatId, prev);
  });

  const sorted = [...bySeat.values()].sort((a, b) =>
    String(a.seatId).localeCompare(String(b.seatId), undefined, { numeric: true }),
  );

  const headers = [
    'Koltuk No',
    'Çakışma Tipi',
  ];
  for (let i = 1; i <= MAX_PARTIES; i += 1) {
    headers.push(`Kişi ${i}`, `Telefon ${i}`, `PNR ${i}`);
  }
  headers.push('Aktarılacak Koltuk', 'Not');

  const aoa = [headers];

  sorted.forEach((group) => {
    let parties = [...group.ticketIds]
      .map((id) => findTicket(tickets, id))
      .filter(Boolean)
      .filter((t) => !isTicketRefunded(t));

    // ticketIds boşsa seatsByTicket'tan doldur (sold_without_ticket hariç)
    if (!parties.length) {
      parties = (seatsByTicket.get(group.seatId) || []).filter((t) => !isTicketRefunded(t));
    }

    const row = [
      group.seatId,
      [...group.types].map(conflictTypeLabel).join(' · '),
    ];

    for (let i = 0; i < MAX_PARTIES; i += 1) {
      const p = ticketParty(parties[i], group.seatId);
      row.push(p.name, p.phone, p.pnr);
    }

    row.push('', group.messages.filter(Boolean).join(' | '));
    aoa.push(row);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Kolon genişlikleri
  ws['!cols'] = [
    { wch: 12 }, // Koltuk
    { wch: 36 }, // Tip
    ...Array.from({ length: MAX_PARTIES }, () => [
      { wch: 22 }, // Kişi
      { wch: 16 }, // Telefon
      { wch: 18 }, // PNR
    ]).flat(),
    { wch: 18 }, // Aktarılacak
    { wch: 48 }, // Not
  ];

  // Başlık satırı freeze
  ws['!freeze'] = { xSplit: 1, ySplit: 1 };
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: aoa.length - 1, c: headers.length - 1 },
    }),
  };

  // Başlık hücresi stilleri (xlsx community — sınırlı destek)
  for (let c = 0; c < headers.length; c += 1) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (!ws[addr]) continue;
    ws[addr].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '202042' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    };
  }

  // Aktarılacak koltuk sütunu vurgusu (boş bırakılacak alan)
  const transferCol = 2 + MAX_PARTIES * 3;
  for (let r = 1; r < aoa.length; r += 1) {
    const addr = XLSX.utils.encode_cell({ r, c: transferCol });
    if (!ws[addr]) ws[addr] = { t: 's', v: '' };
    ws[addr].s = {
      fill: { patternType: 'solid', fgColor: { rgb: 'FFF3CD' } },
      alignment: { horizontal: 'center' },
    };
  }

  // Satır yüksekliği başlık
  ws['!rows'] = [{ hpt: 28 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Cakismalar');

  // Üst bilgi sayfası — kısa
  const info = XLSX.utils.aoa_to_sheet([
    ['Etkinlik', event?.name || ''],
    ['Event ID', eventID || event?.id || ''],
    ['Çakışan koltuk', sorted.length],
    ['Toplam uyarı kaydı', conflicts.length],
    ['Rapor', new Date().toLocaleString('tr-TR')],
    [],
    ['Not', 'Sarı sütun (Aktarılacak Koltuk) elle doldurulacak.'],
  ]);
  info['!cols'] = [{ wch: 18 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, info, 'Bilgi');

  const safeName = String(event?.name || eventID || 'event')
    .replace(/[^\w\-ğüşıöçĞÜŞİÖÇ ]+/gi, '')
    .trim()
    .slice(0, 40)
    .replace(/\s+/g, '-');
  const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
  XLSX.writeFileXLSX(wb, `cakisma-${safeName}-${stamp}.xlsx`);
}
